import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import type { RouterOutputs } from "@alm/api-contracts";
import { DIRECTORY_KINDS, formatDateOnly, type DirectoryEntry, errorWords } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { FileRecipientDialog } from "@/components/app/sending/file-recipient-dialog";
import { PageHeader } from "@/components/app/shell/page-header";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { optionalString, KEEP_PLACE, optionalSearch } from "@/lib/search";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { cn } from "@/lib/utils";
import { useCanWriteNetworkRow } from "@/lib/policy/care-network-policy";
import {
  useCan,
  useCanChangeRow,
  useCanWithoutBuilding,
  useScopeFor,
  useFeature,
} from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { DirectoryEditDialog } from "./-components/directory-edit-dialog";
import { DirectoryFilters, type DirectoryFilterValues } from "./-components/directory-filters";
import {
  CHANNEL_FILTERS,
  correctableSource,
  kindFilterOptions,
  unfiledWhat,
} from "./-components/directory-labels";
import { DirectoryTable } from "./-components/directory-table";
import { UnfiledQueue } from "./-components/unfiled-queue";
import { useZoneFor } from "@/lib/facility/zone";
import { keepWhileSame } from "@/lib/query/keep-while-same";

export const Route = createFileRoute("/_authenticated/_communications/directory/")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; channel?: string; kind?: string } => ({
    q: optionalSearch(search.q),
    channel: optionalString(search.channel) || undefined,
    kind: optionalString(search.kind) || undefined,
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "directory"),
  component: DirectoryPage,
});

type UnfiledSend = RouterOutputs["care"]["directories"]["unfiled"][number];

/** One list of everyone this community writes to. Nothing is stored: every row is read from its
 * owning record, with a link back. */
function DirectoryPage() {
  const { facilityId } = useFacility();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();

  const canManageNetwork = useCanWithoutBuilding("directory:manage");
  // An entry is corrected where its record lives: a building's at that building, a shared one
  // by whoever may write shared rows of its kind.
  const writesNetwork = useCanWriteNetworkRow();
  const keepsFaxBook = useCanChangeRow("fax:send");
  // The fax address book is every fax user's to keep.
  const canKeepFaxBook = useCan("fax:send");
  const faxEnabled = useFeature("fax");

  const { q: term, channel: channelParam, kind: kindParam } = Route.useSearch();
  const refine = (next: { q?: string; channel?: string; kind?: string }) =>
    void navigate({
      search: (prev) => ({ ...prev, ...next }),
      ...KEEP_PLACE,
    });
  const { text, change: setText, settling } = useSearchParamText(term, (q) => refine({ q }), 250);
  const filters: DirectoryFilterValues = {
    search: text,
    channel: CHANNEL_FILTERS.find((entry) => entry.value === channelParam)?.value ?? "all",
    kind: DIRECTORY_KINDS.find((entry) => entry === kindParam) ?? "all",
  };
  const [filing, setFiling] = useState<UnfiledSend | null>(null);
  const [editing, setEditing] = useState<DirectoryEntry | null>(null);
  const [dismissing, setDismissing] = useState<string | null>(null);

  /* "Raw" is a view of the unfiled queue rather than a narrowing of the
     directory, so the directory is not fetched at all while it is open — and
     "raw" must never reach the search input, which has no such channel. */
  const onRaw = filters.channel === "raw";

  const entriesQuery = useQuery({
    ...orpc.care.directories.search.queryOptions({
      input: {
        facilityId,
        search: term || undefined,
        channel:
          filters.channel === "all" || filters.channel === "raw" ? undefined : filters.channel,
        kinds: filters.kind === "all" ? undefined : [filters.kind],
      },
    }),
    enabled: !onRaw,
    placeholderData: keepWhileSame({ facilityId }),
  });
  const searching = settling || entriesQuery.isPlaceholderData;
  const unfiledQuery = useQuery(
    orpc.care.directories.unfiled.queryOptions({ input: { facilityId } }),
  );

  const directoryScope = useScopeFor("directory:read");
  /* Which building a contact belongs to, shown to anybody who can reach more
     than one of them: somebody with one building already knows which it is. */
  const showCommunity =
    facilityId === "all" || directoryScope === null || directoryScope.length > 1;
  const facilities = useQuery({
    ...orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
    enabled: showCommunity,
  });

  const entries: DirectoryEntry[] = entriesQuery.data ?? [];
  /* The queue is filtered here rather than on the server: it is the tail of
     what has been sent, it arrives whole, and a round trip per keystroke would
     buy nothing. */
  const unfiled = (unfiledQuery.data ?? []).filter((send) => {
    if (!onRaw || !term) return true;
    const needle = term.toLowerCase();
    return [send.value, unfiledWhat(send), send.lastSentBy]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(needle));
  });
  const kindOptions = useMemo(
    () => kindFilterOptions(entriesQuery.data ?? []),
    [entriesQuery.data],
  );

  const day = (iso: string) => formatDateOnly(iso, zoneFor(filingFacilityId));
  const facilityName = (id: string | null) =>
    facilities.data?.find((facility) => facility.id === id)?.name ?? "-";
  /** The switcher's "all" is a filter, never a building a new record belongs to. */
  const filingFacilityId = facilityId === "all" ? null : facilityId;
  const zoneFor = useZoneFor();

  const filtered = Boolean(term) || filters.channel !== "all" || filters.kind !== "all";
  // A page left beyond the end of a narrowed list is clamped by the table's paging.
  const changeFilters = (patch: Partial<DirectoryFilterValues>) => {
    if (patch.search !== undefined) setText(patch.search);
    refine({
      ...(patch.channel === undefined
        ? {}
        : { channel: patch.channel === "all" ? undefined : patch.channel }),
      ...(patch.kind === undefined ? {} : { kind: patch.kind === "all" ? undefined : patch.kind }),
    });
  };
  const clearFilters = () => {
    setText("");
    refine({ q: undefined, channel: undefined, kind: undefined });
  };

  const canEdit = (entry: DirectoryEntry) => {
    const source = correctableSource(entry);
    if (!source) return false;
    // The entry's own facilityId also means "merged across buildings"; the record's is the answer.
    const at = entry.sources[0]?.facilityId ?? null;
    // A saved contact lives in the fax address book, which an organization can
    // be sold without: the Directory reads it either way, but correcting it is
    // Fax's write.
    return source.kind === "person" ? faxEnabled && keepsFaxBook(at) : writesNetwork(at);
  };

  const dismiss = useMutation(
    orpc.care.directories.dismissUnfiled.mutationOptions({
      onSuccess: async (_result, input) => {
        toast.success(`${input.value} marked as not a contact`);
        setDismissing(null);
        await queryClient.invalidateQueries({ queryKey: orpc.care.directories.key() });
      },
      onError: (error: Error) => {
        setDismissing(null);
        toast.error(errorWords(error));
      },
    }),
  );

  /* On Raw the header counts what is waiting rather than the directory, which
     is not being shown and whose count would read as the number of rows below
     it. The "to file" tail goes with it: the tab carries that number now. */
  const meta = onRaw
    ? `${unfiled.length} ${unfiled.length === 1 ? "entry" : "entries"} waiting to be filed`
    : entriesQuery.isPending
      ? "Loading…"
      : `${entries.length} ${entries.length === 1 ? "entry" : "entries"}${filtered ? " matching" : ""}${
          unfiled.length > 0 ? `, ${unfiled.length} to file` : ""
        }`;

  return (
    <>
      <PageHeader title="Directory" meta={meta} />

      <DirectoryFilters
        values={filters}
        kindOptions={kindOptions}
        unfiledCount={unfiled.length}
        onChange={changeFilters}
        /* Not on Raw: the queue is not narrowed by anything, so "Clear
           filters" there is a button offering to undo nothing. */
        onClear={filtered && !onRaw ? clearFilters : null}
      />

      {onRaw ? (
        unfiledQuery.isError ? (
          /* A failed read of the queue must not read as an empty queue: what is
             waiting to be filed is exactly what nobody would otherwise notice. */
          <EmptyState
            title="Could not load the queue"
            description={`${errorWords(unfiledQuery.error)} This is not a statement that nothing is waiting.`}
            action={
              <Button variant="outline" onClick={() => void unfiledQuery.refetch()}>
                Try again
              </Button>
            }
          />
        ) : unfiled.length > 0 ? (
          <UnfiledQueue
            sends={unfiled}
            day={day}
            canFile={canManageNetwork || (canKeepFaxBook && faxEnabled)}
            canDismiss={canManageNetwork}
            dismissing={dismissing}
            onFile={setFiling}
            onDismiss={(send) => {
              setDismissing(send.value);
              dismiss.mutate({ value: send.value });
            }}
          />
        ) : (
          <EmptyState
            title="Nothing waiting to be filed"
            description="Numbers and addresses somebody sent to without a directory entry appear here."
          />
        )
      ) : entriesQuery.isPending || (searching && entries.length === 0) ? (
        <Skeleton className="h-64 w-full" />
      ) : entriesQuery.error ? (
        <EmptyState
          title="Could not load the directory"
          description={errorWords(entriesQuery.error)}
          action={
            <Button variant="outline" onClick={() => void entriesQuery.refetch()}>
              Try again
            </Button>
          }
        />
      ) : entries.length === 0 ? (
        <EmptyState
          title={filtered ? "Nothing matches" : "Nothing to show yet"}
          description={
            filtered
              ? "Widen the search or clear a filter."
              : "The directory reads from your physicians, pharmacies, institutions and residents' contacts. Add any of those and they appear here."
          }
          action={
            filtered ? (
              <Button variant="outline" onClick={clearFilters}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div
          aria-busy={searching || undefined}
          className={cn("transition-opacity", searching && "opacity-60")}
        >
          <DirectoryTable
            entries={entries}
            day={day}
            showCommunity={showCommunity}
            facilityName={facilityName}
            canEdit={canEdit}
            onEdit={setEditing}
            onOpen={(href) => void navigate({ to: href })}
          />
        </div>
      )}

      <FileRecipientDialog
        open={filing !== null}
        onOpenChange={(open) => !open && setFiling(null)}
        /* Which channel the send actually went out on. Only fax is logged
           today, so this reads "fax" either way — but the record it files
           should not be decided by a constant. */
        channel={filing?.channel === "email" ? "email" : "fax"}
        value={filing?.value ?? ""}
        facilityId={filingFacilityId}
      />
      <DirectoryEditDialog entry={editing} onOpenChange={(open) => !open && setEditing(null)} />
    </>
  );
}
